import { t } from "@lingui/core/macro";
import { DateRangeActions } from "@/Components/DateRangeActions";
import { api } from "@/lib/api-client";
import { useDebouncedCallback } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { RiSearch2Line } from "react-icons/ri";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { CreateAccount } from "./CreateAccount";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { Spinner } from "@heroui/react";
import {
  LimePageHeader,
  LimePageHeaderProps,
} from "@/Components/LimePageHeader";
import { LimePageTabs } from "@/Components/LimePageTabs";
import { AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";

const getCurrentTab = (pathName: string) => {
  const splitPath = pathName.split("/");
  const stripeIndex = splitPath.findIndex((p) => p === "stripe");
  const tab = splitPath[stripeIndex + 1];

  return tab;
};

const getCurrentSettingsSubTab = (pathName: string) => {
  const splitPath = pathName.split("/");
  const settingsIndex = splitPath.findIndex((p) => p === "settings");
  const tab = splitPath[settingsIndex + 1];

  return tab;
};

export type StripeOutletContextProps = {
  general: {
    dateRange: [string | undefined, string | undefined];
  };
  payments: {
    search: string;
  };
};

export const Stripe = () => {
  const activeTab = getCurrentTab(window.location.pathname);
  const activeSettingsSubTab = getCurrentSettingsSubTab(
    window.location.pathname,
  );

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: accountData, isPending } = api.stripe.useGetStripeAccount();

  const [search, setSearch] = useState<string>(searchParams.get("q") ?? "");
  const setSearchDebounced = useDebouncedCallback((query: string) => {
    setSearchParams((prev) => {
      prev.set("q", query);
      return prev;
    });
  }, 400);
  const handleSearchChange = (val: string) => {
    setSearch(val);
    setSearchDebounced(val);
  };

  const handleChangeDateRange = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      const startDateString = startDate
        ? dayjs(startDate).format("YYYY-MM-DD")
        : undefined;
      const endDateString = endDate
        ? dayjs(endDate).add(1, "day").format("YYYY-MM-DD")
        : undefined;

      setSearchParams((prev) => {
        prev.set("startDate", startDateString ?? "");
        prev.set("endDate", endDateString ?? "");
        return prev;
      });
    },
    [],
  );

  let subPageHeader: LimePageHeaderProps["subPage"] = undefined;
  if (activeSettingsSubTab === "services") {
    subPageHeader = {
      title: t`Nastavitve spletnega plačevanja`,
      onBackButtonClick: () => navigate("./settings", { relative: "path" }),
    };
  }

  const rightSection = (() => {
    if (activeTab === "general") {
      return (
        <div className="mt-2 md:mt-0">
          <DateRangeActions
            handleChangeDateRange={handleChangeDateRange}
            disabledDateRangeOptions={["1d", "all"]}
          />
        </div>
      );
    }

    if (activeTab === "payments") {
      return (
        <LimeInput
          placeholder={t`Išči naročilo`}
          startContent={<RiSearch2Line />}
          className="mt-2 w-full px-4 md:mt-0 md:max-w-[350px] md:px-0"
          value={search}
          onValueChange={handleSearchChange}
        />
      );
    }

    return <></>;
  })();

  const statusLeftSection = (() => {
    if (accountData?.requirements == null) {
      return <Spinner size="sm" />;
    }

    if (accountData?.requirements?.isDisabled) {
      return <AlertCircle className="text-red-500" size={16} />;
    }

    if (accountData?.requirements?.hasDueRequirements) {
      return <AlertTriangle className="text-yellow-500" size={16} />;
    }

    return <CheckCircle className="text-green-500" size={16} />;
  })();

  return (
    <div>
      <LimePageHeader title={t`Spletno plačevanje`} subPage={subPageHeader} />

      {!subPageHeader && (
        <LimePageTabs
          activeTab={activeTab}
          onChangeTab={(tab) => {
            if (!tab) {
              navigate(".", {
                relative: "path",
              });
            } else {
              navigate(tab, {
                relative: "path",
              });
            }
          }}
          rightSection={rightSection}
          tabs={[
            { label: t`Splošno`, value: "general" },
            { label: t`Pregled plačil`, value: "payments" },
            { label: t`Nastavitve`, value: "settings" },
            {
              label: t`Status`,
              value: "status",
              leftSection: statusLeftSection,
            },
          ]}
        />
      )}

      {isPending ? (
        <div className="mt-4 flex items-center justify-center">
          <Spinner />
        </div>
      ) : accountData?.stripeAccountId != null ? (
        <div className="mt-2 px-4 md:px-8">
          <Outlet />
        </div>
      ) : (
        <CreateAccount />
      )}
    </div>
  );
};
